/**
 * ShareToChatModal — Share a CRM entity (lead, task, project, deal) to a team chat
 *
 * Reuses the same Notion-style layout as ForwardMessageModal.
 * Accepts entity data, lets user pick a chat, add an optional note, and sends.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Users, Send, UserPlus, CheckSquare, FolderKanban, Receipt } from 'lucide-react';
import { useTeamChatStore } from '../../lib/stores/teamChatStore';
import { useAuth } from '../../lib/auth';
import toast from 'react-hot-toast';

export type ShareEntityType = 'lead' | 'task' | 'project' | 'deal';

export interface ShareToChatModalProps {
  entityType: ShareEntityType;
  entityId: string;
  entityData: Record<string, unknown>;
  onClose: () => void;
}

const entityConfig: Record<ShareEntityType, { label: string; icon: React.FC<any>; color: string; bg: string }> = {
  lead: { label: 'Lead', icon: UserPlus, color: '#EA580C', bg: 'rgba(234,88,12,0.08)' },
  task: { label: 'Task', icon: CheckSquare, color: '#7C3AED', bg: 'rgba(124,58,237,0.08)' },
  project: { label: 'Progetto', icon: FolderKanban, color: '#16A34A', bg: 'rgba(22,163,74,0.08)' },
  deal: { label: 'Offerta', icon: Receipt, color: '#2383E2', bg: 'rgba(35,131,226,0.08)' },
};

function getEntityTitle(entityType: ShareEntityType, data: Record<string, unknown>): string {
  switch (entityType) {
    case 'lead': return (data.name as string) || 'Lead';
    case 'task': return (data.title as string) || 'Task';
    case 'project': return (data.name as string) || 'Progetto';
    case 'deal': return (data.title as string) || 'Offerta';
    default: return 'Elemento';
  }
}

function getEntitySubtitle(entityType: ShareEntityType, data: Record<string, unknown>): string {
  switch (entityType) {
    case 'lead': return [data.email, data.phone].filter(Boolean).join(' · ') || '';
    case 'task': return [data.project_name, data.assignee].filter(Boolean).join(' · ') || '';
    case 'project': return [data.client_name, data.status].filter(Boolean).join(' · ') || '';
    case 'deal': {
      const amount = data.amount ? `€${Number(data.amount).toLocaleString('it-IT')}` : '';
      return [data.client_name, amount].filter(Boolean).join(' · ') || '';
    }
    default: return '';
  }
}

export function ShareToChatModal({ entityType, entityId, entityData, onClose }: ShareToChatModalProps) {
  const { userProfile } = useAuth();
  const { chats, sendMessage, selectChat, setWidgetState } = useTeamChatStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);

  const config = entityConfig[entityType];
  const Icon = config.icon;

  const getChatDisplayName = useCallback((c: typeof chats[0]) => {
    if (c.is_group && c.name) return c.name;
    const other = c.participants?.find(p => p.user_id !== userProfile?.id);
    return other?.user?.full_name || 'Chat';
  }, [userProfile?.id]);

  const filteredChats = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return chats.filter(c => !q || getChatDisplayName(c).toLowerCase().includes(q));
  }, [chats, searchQuery, getChatDisplayName]);

  const handleSend = useCallback(async () => {
    if (!selectedChatId) return;
    setSending(true);
    try {
      const content = JSON.stringify({
        entity_type: entityType,
        entity_id: entityId,
        entity_data: entityData,
        note: note.trim() || null,
      });
      await sendMessage(selectedChatId, content, null, undefined, 'crm_share');
      toast.success(`${config.label} condiviso in chat`);
      onClose();
      // Open the chat after sharing
      await selectChat(selectedChatId);
      setWidgetState('expanded');
    } catch {
      toast.error('Errore nella condivisione');
    } finally {
      setSending(false);
    }
  }, [selectedChatId, entityType, entityId, entityData, note, sendMessage, onClose, config.label]);

  const getInitials = (name: string) =>
    name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  return createPortal(
    <>
      <div className="fixed inset-0 z-[9998] bg-black/40" onClick={onClose} />
      <div
        className="fixed z-[9999] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 max-h-[75vh] bg-white rounded-xl flex flex-col"
        style={{ border: '1px solid rgba(55,53,47,0.09)', boxShadow: 'rgba(55,53,47,0.1) 0px 8px 32px' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid rgba(55,53,47,0.09)' }}>
          <span className="font-semibold text-sm" style={{ color: '#37352F' }}>Condividi in chat</span>
          <button onClick={onClose} className="p-1 rounded hover:bg-[rgba(55,53,47,0.06)]">
            <X className="w-4 h-4" style={{ color: '#787774' }} strokeWidth={1.5} />
          </button>
        </div>

        {/* Entity preview */}
        <div className="px-4 py-2.5 flex items-center gap-2.5" style={{ backgroundColor: config.bg, borderBottom: '1px solid rgba(55,53,47,0.06)' }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: config.bg }}>
            <Icon className="w-4 h-4" style={{ color: config.color }} strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold truncate" style={{ color: '#37352F' }}>
              {getEntityTitle(entityType, entityData)}
            </div>
            {getEntitySubtitle(entityType, entityData) && (
              <div className="text-[11px] truncate" style={{ color: '#787774' }}>
                {getEntitySubtitle(entityType, entityData)}
              </div>
            )}
          </div>
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0" style={{ color: config.color, backgroundColor: config.bg, border: `1px solid ${config.color}30` }}>
            {config.label}
          </span>
        </div>

        {/* Note */}
        <div className="px-4 py-2" style={{ borderBottom: '1px solid rgba(55,53,47,0.06)' }}>
          <input
            type="text"
            placeholder="Aggiungi una nota... (opzionale)"
            value={note}
            onChange={e => setNote(e.target.value)}
            className="w-full text-sm rounded-md outline-none px-0 py-1"
            style={{ color: '#37352F', fontSize: '13px' }}
          />
        </div>

        {/* Search */}
        <div className="px-3 py-2" style={{ borderBottom: '1px solid rgba(55,53,47,0.06)' }}>
          <div className="relative flex items-center">
            <Search className="absolute left-2.5 w-3.5 h-3.5 pointer-events-none" style={{ color: '#9B9A97' }} strokeWidth={1.5} />
            <input
              type="text"
              placeholder="Cerca chat o persona..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-sm rounded-md outline-none"
              style={{ backgroundColor: 'rgba(55,53,47,0.06)', color: '#37352F', fontSize: '13px', border: '1px solid transparent' }}
              autoFocus
            />
          </div>
        </div>

        {/* Chat list */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {filteredChats.length === 0 ? (
            <p className="text-xs px-4 py-6 text-center" style={{ color: '#9B9A97' }}>Nessuna chat</p>
          ) : (
            filteredChats.map(c => {
              const name = getChatDisplayName(c);
              const isSelected = c.id === selectedChatId;
              return (
                <button
                  key={c.id}
                  onClick={() => setSelectedChatId(c.id)}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-left transition-colors"
                  style={{ backgroundColor: isSelected ? 'rgba(35,131,226,0.1)' : 'transparent' }}
                  onMouseEnter={e => !isSelected && (e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.04)')}
                  onMouseLeave={e => !isSelected && (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  {c.is_group ? (
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(35,131,226,0.1)' }}>
                      <Users className="w-4 h-4" style={{ color: '#2383E2' }} strokeWidth={1.5} />
                    </div>
                  ) : (
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium" style={{ backgroundColor: 'rgba(55,53,47,0.08)', color: '#37352F' }}>
                      {getInitials(name)}
                    </div>
                  )}
                  <span className="text-[13px] truncate" style={{ color: isSelected ? '#2383E2' : '#37352F', fontWeight: isSelected ? 600 : 500 }}>
                    {name}
                  </span>
                </button>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 flex justify-end" style={{ borderTop: '1px solid rgba(55,53,47,0.09)' }}>
          <button
            onClick={handleSend}
            disabled={!selectedChatId || sending}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50"
            style={{ backgroundColor: '#2383E2' }}
          >
            <Send className="w-4 h-4" strokeWidth={1.5} />
            {sending ? 'Invio...' : 'Condividi'}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}
